import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderOpen, Loader2, ArrowRight, X, Play, Sparkles, AlertCircle,
  CheckCircle, Image as ImageIcon, Wand2,
} from 'lucide-react';
import { LoginModal } from './LoginModal';
import { useToast } from '@/hooks/use-toast';
import { useAuthContext } from '@/contexts/AuthContext';

// Anonymous home-page demo flow — see specs/plus-feat-home-demo.md
//
// The 10-second cap is server-enforced; this component is allowed to
// trust the server to chop the clip. The scrubber just hands a startSec
// hint along with the upload — server clamps to [0, sourceDuration - 10].

const MAX_SOURCE_BYTES = 500 * 1024 * 1024; // 500MB
const MAX_SOURCE_DURATION_SEC = 5 * 60;     // 5 min
const DEMO_WINDOW_SEC = 10;
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 240; // 8 min

type AudioMode = 'natural' | 'light' | 'strong';
type BgMode = 'greenscreen' | 'image';
type State = 'idle' | 'loaded' | 'uploading' | 'processing' | 'done' | 'failed' | 'rate_limited';

interface AvailabilityResult {
  available: boolean;
  runsToday?: number;
  capRuns?: number;
}

export const HomeDemoSection: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated } = useAuthContext();

  const [available, setAvailable] = useState<AvailabilityResult | null>(null);
  const [state, setState] = useState<State>('idle');
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [sourceDuration, setSourceDuration] = useState(0);
  const [startSec, setStartSec] = useState(0);
  const [bgMode, setBgMode] = useState<BgMode>('greenscreen');
  const [bgImageFile, setBgImageFile] = useState<File | null>(null);
  const [bgStock, setBgStock] = useState<string | null>(null); // picked studio backdrop filename
  const [audioMode, setAudioMode] = useState<AudioMode>('natural');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [stage, setStage] = useState<string>('');
  const [processingPct, setProcessingPct] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [rateLimitMsg, setRateLimitMsg] = useState<string | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [downloadUnlocked, setDownloadUnlocked] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const pollAbort = useRef<{ cancelled: boolean }>({ cancelled: false });

  // Kill switch — fetch on mount, hide section if not available
  useEffect(() => {
    let cancelled = false;
    fetch('/api/demo/availability')
      .then(r => r.json())
      .then(d => { if (!cancelled) setAvailable(d); })
      .catch(() => { if (!cancelled) setAvailable({ available: false }); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (isAuthenticated) setDownloadUnlocked(true);
  }, [isAuthenticated]);

  // Build object URL for the local preview when a file is loaded; revoke
  // it on unload or replacement so we don't leak.
  useEffect(() => {
    if (!sourceFile) {
      if (sourceUrl) URL.revokeObjectURL(sourceUrl);
      setSourceUrl(null);
      return;
    }
    const url = URL.createObjectURL(sourceFile);
    setSourceUrl(url);
    return () => URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFile]);

  const reset = () => {
    pollAbort.current.cancelled = true;
    setState('idle');
    setSourceFile(null);
    setBgImageFile(null);
    setBgStock(null);
    setStartSec(0);
    setUploadProgress(0);
    setStage('');
    setProcessingPct(0);
    setPreviewUrl(null);
    setErrorMsg(null);
    setRateLimitMsg(null);
    setDownloadUnlocked(isAuthenticated);
  };

  const handleFile = async (file: File) => {
    setErrorMsg(null);
    if (!file.type.startsWith('video/')) {
      toast({ title: 'Pick a video file', variant: 'destructive' });
      return;
    }
    if (file.size > MAX_SOURCE_BYTES) {
      toast({
        title: 'File too large',
        description: 'Max 500MB. Try compressing the clip first.',
        variant: 'destructive',
      });
      return;
    }
    setSourceFile(file);
    setStartSec(0);
    setState('loaded');
  };

  const onVideoLoaded = () => {
    if (!videoRef.current) return;
    const dur = videoRef.current.duration;
    if (!Number.isFinite(dur) || dur <= 0) return;
    setSourceDuration(dur);
    if (dur > MAX_SOURCE_DURATION_SEC) {
      toast({
        title: 'Clip too long',
        description: 'Demo accepts up to 5 minutes. Trim it down first.',
        variant: 'destructive',
      });
      setSourceFile(null);
      setState('idle');
    }
  };

  const uploadToS3 = (file: File, url: string, onProgress: (p: number) => void) =>
    new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', e => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });
      xhr.addEventListener('load', () => xhr.status < 400 ? resolve() : reject(new Error('Upload failed')));
      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });

  const pollStatus = async (jobId: string) => {
    pollAbort.current = { cancelled: false };
    const stageLabels: Record<string, string> = {
      queued: 'Queued',
      downloading: 'Fetching clip',
      trimming: 'Trimming to 10 seconds',
      processing: 'Running AI',
      compositing: 'Compositing',
      completed: 'Done',
    };
    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
      if (pollAbort.current.cancelled) return;
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      const res = await fetch(`/api/demo/status/${jobId}`);
      if (!res.ok) continue;
      const data = await res.json();
      setStage(stageLabels[data.stage] || data.stage || 'Working');
      setProcessingPct(data.progress || Math.min(95, attempt * 2));
      if (data.status === 'completed' && data.previewUrl) {
        setPreviewUrl(data.previewUrl);
        setState('done');
        return;
      }
      if (data.status === 'failed') {
        setErrorMsg(data.error || 'Something went wrong. Try a different clip.');
        setState('failed');
        return;
      }
    }
    setErrorMsg('Demo timed out. Try a shorter clip.');
    setState('failed');
  };

  const handleGenerate = async () => {
    if (!sourceFile) return;
    setState('uploading');
    setUploadProgress(0);
    setErrorMsg(null);
    try {
      // 1. Get presigned URL for source
      const urlRes = await fetch('/api/demo/upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: sourceFile.name,
          fileType: sourceFile.type,
          fileSize: sourceFile.size,
          kind: 'source',
        }),
      });
      if (urlRes.status === 429) {
        const data = await urlRes.json();
        const reason = data.error;
        const resetAt = data.resetAt ? new Date(data.resetAt).toLocaleString() : 'tomorrow';
        const msg =
          reason === 'daily_cap' || reason === 'demo_paused'
            ? 'The free demo is paused for the day. Sign up for a free trial to keep going.'
            : reason === 'ip_limit'
              ? `You already ran a free demo from this device. Sign in for 3 more, or try the free trial.`
              : reason === 'account_limit'
                ? `You've used your 3 free demos today. They reset at ${resetAt}.`
                : 'Demo limit reached.';
        setRateLimitMsg(msg);
        setState('rate_limited');
        return;
      }
      if (!urlRes.ok) {
        const data = await urlRes.json().catch(() => ({}));
        throw new Error(data.error || 'Could not start demo');
      }
      const { uploadUrl, fingerprint } = await urlRes.json();

      // 2. PUT source to S3 with progress
      await uploadToS3(sourceFile, uploadUrl, setUploadProgress);

      // 3. (Optional) Upload BG image — skipped when a pre-made studio backdrop
      //    is picked (those resolve server-side, no upload needed).
      let bgImageKey: string | undefined;
      if (bgMode === 'image' && !bgStock && bgImageFile) {
        const bgRes = await fetch('/api/demo/upload-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: bgImageFile.name,
            fileType: bgImageFile.type,
            fileSize: bgImageFile.size,
            kind: 'bg-image',
          }),
        });
        if (bgRes.ok) {
          const { uploadUrl: bgUrl, key } = await bgRes.json();
          await uploadToS3(bgImageFile, bgUrl, () => {});
          bgImageKey = key;
        }
      }

      // 4. Kick off the pipeline
      setState('processing');
      setStage('Queued');
      const previewRes = await fetch('/api/demo/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fingerprint,
          startSec: Math.floor(startSec),
          bgMode,
          bgImageKey,
          bgStock: bgStock || undefined,
          audioMode,
        }),
      });
      if (!previewRes.ok) {
        const data = await previewRes.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to start preview');
      }
      const { jobId } = await previewRes.json();
      await pollStatus(jobId);
    } catch (err: any) {
      setErrorMsg(err.message || 'Something went wrong');
      setState('failed');
    }
  };

  // If the kill switch is tripped, render nothing — section disappears
  if (available === null) return null;
  if (!available.available) return null;

  // Render
  return (
    <>
      <section
        className="relative overflow-hidden"
        style={{
          margin: '40px 0 28px',
          padding: 'clamp(28px, 4vw, 44px)',
          borderRadius: 'var(--hj-r-lg)',
          background: 'var(--hj-surface-1)',
          border: '1px solid var(--hj-hairline)',
        }}
      >
        <div className="flex items-start justify-between gap-6 flex-wrap" style={{ marginBottom: 22 }}>
          <div style={{ maxWidth: 580 }}>
            <div className="mono-label inline-flex items-center gap-1.5" style={{ marginBottom: 10, color: 'var(--hj-yellow-ink)' }}>
              <Sparkles className="w-3.5 h-3.5" />
              Sample audition enhance · Free preview
            </div>
            <h2
              className="font-hj-display m-0"
              style={{
                fontSize: 'clamp(28px, 3.4vw, 40px)',
                lineHeight: 1.05,
                color: 'var(--hj-ink)',
                letterSpacing: '-0.025em',
                fontWeight: 800,
              }}
            >
              Drop a clip. See the magic.
            </h2>
            <p className="m-0" style={{ marginTop: 10, color: 'var(--hj-ink)', fontSize: 14.5, lineHeight: 1.5 }}>
              We'll background-remove and audio-enhance 10 seconds of your take so you can see the result before signing up.
            </p>
          </div>
          {state !== 'idle' && state !== 'rate_limited' && (
            <button
              type="button"
              onClick={reset}
              aria-label="Start over"
              style={{
                background: 'transparent',
                border: '1px solid var(--hj-hairline)',
                color: 'var(--hj-ink-3)',
                padding: '7px 14px',
                borderRadius: 999,
                fontSize: 12.5,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Start over
            </button>
          )}
        </div>

        {state === 'idle' && <IdleDropZone onFile={handleFile} />}

        {state === 'loaded' && sourceUrl && (
          <LoadedEditor
            sourceUrl={sourceUrl}
            videoRef={videoRef}
            onVideoLoaded={onVideoLoaded}
            duration={sourceDuration}
            startSec={startSec}
            setStartSec={setStartSec}
            bgMode={bgMode}
            setBgMode={setBgMode}
            bgImageFile={bgImageFile}
            setBgImageFile={setBgImageFile}
            bgStock={bgStock}
            setBgStock={setBgStock}
            audioMode={audioMode}
            setAudioMode={setAudioMode}
            onGenerate={handleGenerate}
          />
        )}

        {state === 'uploading' && (
          <ProgressBlock
            heading="Uploading clip…"
            pct={uploadProgress}
            sublabel={`${uploadProgress}%`}
          />
        )}

        {state === 'processing' && (
          <ProgressBlock
            heading={stage || 'Working'}
            pct={processingPct}
            sublabel="Usually 60–90 seconds. The AI is doing real work."
          />
        )}

        {state === 'done' && previewUrl && (
          <DoneBlock
            previewUrl={previewUrl}
            downloadUnlocked={downloadUnlocked}
            onSignupClick={() => setShowLogin(true)}
            onAuditionClick={() => navigate(isAuthenticated ? '/audition' : '/trial')}
          />
        )}

        {state === 'failed' && (
          <FailedBlock errorMsg={errorMsg || 'Something went wrong.'} onReset={reset} />
        )}

        {state === 'rate_limited' && (
          <RateLimitedBlock
            msg={rateLimitMsg || 'Demo limit reached.'}
            isAuthenticated={isAuthenticated}
            onSignupClick={() => setShowLogin(true)}
            onTrialClick={() => navigate('/trial')}
          />
        )}
      </section>

      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSuccess={() => {
          setShowLogin(false);
          setDownloadUnlocked(true);
        }}
      />
    </>
  );
};

// ── Sub-components ────────────────────────────────────────────────────

const IdleDropZone: React.FC<{ onFile: (f: File) => void }> = ({ onFile }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  return (
    <div
      className="text-center cursor-pointer transition-colors"
      onClick={() => inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) onFile(file);
      }}
      style={{
        padding: '52px 24px',
        borderRadius: 'var(--hj-r-md)',
        background: dragOver ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
        border: `1.5px dashed ${dragOver ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
      }}
    >
      <div
        className="mx-auto flex items-center justify-center"
        style={{
          width: 56,
          height: 56,
          borderRadius: 999,
          background: 'var(--hj-yellow-tint)',
          color: 'var(--hj-yellow-ink)',
          marginBottom: 14,
        }}
      >
        <FolderOpen size={24} strokeWidth={1.8} />
      </div>
      <p
        className="font-hj-display m-0"
        style={{ fontSize: 20, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}
      >
        Drag a clip here, or click to pick
      </p>
      <p className="m-0" style={{ marginTop: 6, fontSize: 13, color: 'var(--hj-ink-2)' }}>
        Up to 500MB · 5 minutes max · we only process 10 seconds
      </p>
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
        }}
      />
    </div>
  );
};

const LoadedEditor: React.FC<{
  sourceUrl: string;
  videoRef: React.RefObject<HTMLVideoElement>;
  onVideoLoaded: () => void;
  duration: number;
  startSec: number;
  setStartSec: (n: number) => void;
  bgMode: BgMode;
  setBgMode: (m: BgMode) => void;
  bgImageFile: File | null;
  setBgImageFile: (f: File | null) => void;
  bgStock: string | null;
  setBgStock: (s: string | null) => void;
  audioMode: AudioMode;
  setAudioMode: (m: AudioMode) => void;
  onGenerate: () => void;
}> = ({
  sourceUrl, videoRef, onVideoLoaded, duration, startSec, setStartSec,
  bgMode, setBgMode, bgImageFile, setBgImageFile, bgStock, setBgStock, audioMode, setAudioMode, onGenerate,
}) => {
  const showScrubber = duration > DEMO_WINDOW_SEC;
  const maxStart = Math.max(0, duration - DEMO_WINDOW_SEC);
  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  };
  const bgImageInputRef = useRef<HTMLInputElement>(null);

  // Pre-made studio backdrops (served from /stock-backgrounds/). Same set as the
  // Audition Enhancer — one-click, no upload.
  const STUDIO_BACKDROPS = [
    { file: 'studio-gray-soft.png',  label: 'Gray · Soft' },
    { file: 'studio-gray-warm.png',  label: 'Gray · Warm' },
    { file: 'studio-white-soft.png', label: 'White · Soft' },
    { file: 'studio-white-warm.png', label: 'White · Warm' },
    { file: 'studio-blue-soft.png',  label: 'Blue · Soft' },
    { file: 'studio-blue-deep.png',  label: 'Blue · Deep' },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-3">
          <video
            ref={videoRef}
            src={sourceUrl}
            controls
            muted
            playsInline
            onLoadedMetadata={onVideoLoaded}
            onTimeUpdate={e => {
              // Auto-clamp playback to within the selected window so the user
              // can see exactly what'll be processed
              const t = (e.target as HTMLVideoElement).currentTime;
              if (t > startSec + DEMO_WINDOW_SEC) {
                (e.target as HTMLVideoElement).currentTime = startSec;
              }
            }}
            className="w-full"
            style={{
              borderRadius: 'var(--hj-r-sm)',
              background: '#000',
              maxHeight: 320,
            }}
          />
          {/* Start-time picker — always show this block so the user knows the
              tool exists. For clips ≤ 10s the range is disabled with a
              "whole clip" hint instead of vanishing entirely. */}
          <div
            style={{
              padding: 14,
              borderRadius: 'var(--hj-r-sm)',
              background: 'var(--hj-surface-2)',
              border: '1px solid var(--hj-hairline-soft)',
            }}
          >
            <div className="flex items-baseline justify-between" style={{ marginBottom: 10 }}>
              <span
                className="font-hj-display"
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--hj-ink)',
                  letterSpacing: '-0.01em',
                }}
              >
                Pick your 10-second window
              </span>
              <span
                className="tabular"
                style={{
                  fontSize: 13,
                  color: 'var(--hj-yellow-ink)',
                  fontFamily: 'var(--hj-font-mono)',
                  fontWeight: 600,
                }}
              >
                {fmt(startSec)} → {fmt(Math.min(duration || DEMO_WINDOW_SEC, startSec + DEMO_WINDOW_SEC))}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={Math.max(0.1, maxStart)}
              step={0.5}
              value={startSec}
              disabled={!showScrubber}
              onChange={e => {
                const v = parseFloat(e.target.value);
                setStartSec(v);
                if (videoRef.current) videoRef.current.currentTime = v;
              }}
              className="w-full"
              style={{ accentColor: 'var(--hj-blue)', cursor: showScrubber ? 'pointer' : 'not-allowed' }}
            />
            <p
              className="m-0"
              style={{ marginTop: 8, fontSize: 11.5, color: 'var(--hj-ink-2)', lineHeight: 1.4 }}
            >
              {showScrubber
                ? 'Drag to choose where the 10-second clip starts. The video preview jumps with the handle.'
                : duration > 0
                  ? 'Your clip is under 10 seconds — the whole thing will be used.'
                  : 'Loading…'}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div>
            <div className="mono-label" style={{ marginBottom: 8, color: 'var(--hj-ink-3)' }}>
              Background
            </div>
            <div className="grid grid-cols-2 gap-2">
              <OptionPill
                active={bgMode === 'greenscreen'}
                onClick={() => { setBgMode('greenscreen'); setBgStock(null); }}
                icon={<Wand2 className="w-3.5 h-3.5" />}
                label="Green screen"
                sub="Editable in any NLE"
              />
              <OptionPill
                active={bgMode === 'image'}
                onClick={() => setBgMode('image')}
                icon={<ImageIcon className="w-3.5 h-3.5" />}
                label="Studio backdrop"
                sub="Studio-grade or your own"
              />
            </div>
            {bgMode === 'image' && (
              <div style={{ marginTop: 12 }}>
                {/* Studio backdrops — one-click, no upload */}
                <div className="mono-label" style={{ marginBottom: 8, color: 'var(--hj-ink-3)' }}>Studio backdrops</div>
                <div className="grid grid-cols-3 gap-2">
                  {STUDIO_BACKDROPS.map((bd) => {
                    const active = bgStock === bd.file;
                    return (
                      <button
                        key={bd.file}
                        type="button"
                        onClick={() => { setBgStock(bd.file); setBgImageFile(null); }}
                        className="relative aspect-video overflow-hidden flex flex-col"
                        style={{
                          padding: 5,
                          borderRadius: 'var(--hj-r-sm)',
                          background: 'var(--hj-surface-2)',
                          border: `1.5px solid ${active ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}`,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        <img src={`/stock-backgrounds/${bd.file}`} alt={bd.label} className="flex-1 w-full object-cover" style={{ borderRadius: 4 }} />
                        <div className="mono-label" style={{ marginTop: 5, fontSize: 8.5 }}>{bd.label}</div>
                        {active && (
                          <div className="absolute flex items-center justify-center" style={{ top: 6, right: 6, width: 16, height: 16, borderRadius: 999, background: 'var(--hj-blue)', color: 'oklch(0.18 0.005 70)' }}>
                            <CheckCircle className="w-2.5 h-2.5" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Or upload your own */}
                <div className="mono-label" style={{ margin: '14px 0 8px', color: 'var(--hj-ink-3)' }}>Or upload your own</div>
                <input
                  ref={bgImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => { setBgImageFile(e.target.files?.[0] || null); setBgStock(null); }}
                />
                <button
                  type="button"
                  onClick={() => bgImageInputRef.current?.click()}
                  className="inline-flex items-center gap-2 transition-colors"
                  style={{
                    padding: '8px 14px',
                    borderRadius: 999,
                    background: 'var(--hj-surface-2)',
                    border: `1px dashed ${bgImageFile ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
                    color: 'var(--hj-ink-2)',
                    fontSize: 12.5,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    minHeight: 36,
                  }}
                >
                  <ImageIcon className="w-3.5 h-3.5" />
                  {bgImageFile ? bgImageFile.name : 'Choose backdrop image'}
                </button>
              </div>
            )}
          </div>

          <div>
            <div className="mono-label" style={{ marginBottom: 8, color: 'var(--hj-ink-3)' }}>
              Audio enhance
            </div>
            <div className="flex flex-wrap gap-2">
              {(['natural', 'light', 'strong'] as AudioMode[]).map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setAudioMode(m)}
                  className="inline-flex items-center capitalize"
                  style={{
                    padding: '7px 14px',
                    borderRadius: 999,
                    background: audioMode === m ? 'var(--hj-blue)' : 'transparent',
                    border: `1px solid ${audioMode === m ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
                    color: audioMode === m ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink-2)',
                    fontSize: 12.5,
                    fontWeight: audioMode === m ? 700 : 500,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    minHeight: 36,
                  }}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={onGenerate}
            className="inline-flex items-center justify-center gap-2"
            style={{
              marginTop: 8,
              padding: '12px 22px',
              borderRadius: 999,
              background: 'var(--hj-blue)',
              border: '1px solid var(--hj-blue)',
              color: 'oklch(0.18 0.005 70)',
              fontSize: 14,
              fontWeight: 700,
              fontFamily: 'inherit',
              cursor: 'pointer',
              minHeight: 44,
            }}
          >
            Generate preview
            <ArrowRight size={14} strokeWidth={2.4} />
          </button>
        </div>
      </div>
    </div>
  );
};

const OptionPill: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  sub: string;
}> = ({ active, onClick, icon, label, sub }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-left transition-colors"
    style={{
      padding: 12,
      borderRadius: 'var(--hj-r-sm)',
      background: active ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
      border: `1px solid ${active ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
      color: 'var(--hj-ink)',
      fontFamily: 'inherit',
      cursor: 'pointer',
    }}
  >
    <div className="inline-flex items-center gap-1.5" style={{ fontSize: 13, fontWeight: 600 }}>
      <span style={{ color: active ? 'var(--hj-blue)' : 'var(--hj-ink-3)' }}>{icon}</span>
      {label}
    </div>
    <div style={{ marginTop: 2, fontSize: 11.5, color: 'var(--hj-ink-3)' }}>{sub}</div>
  </button>
);

const ProgressBlock: React.FC<{ heading: string; pct: number; sublabel: string }> = ({ heading, pct, sublabel }) => (
  <div
    className="flex flex-col items-center text-center"
    style={{
      padding: '48px 24px',
      borderRadius: 'var(--hj-r-md)',
      background: 'var(--hj-surface-2)',
      border: '1px solid var(--hj-hairline)',
    }}
  >
    <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />
    <p
      className="font-hj-display m-0"
      style={{ marginTop: 12, fontSize: 18, color: 'var(--hj-ink)', letterSpacing: '-0.02em' }}
    >
      {heading}
    </p>
    <div className="w-full" style={{ maxWidth: 420, marginTop: 14 }}>
      <div
        className="w-full overflow-hidden"
        style={{ height: 6, borderRadius: 999, background: 'var(--hj-surface-3)' }}
      >
        <div
          className="h-full transition-all"
          style={{ width: `${pct}%`, background: 'var(--hj-blue)', borderRadius: 999 }}
        />
      </div>
    </div>
    <p className="mono-label m-0" style={{ marginTop: 10, color: 'var(--hj-ink-3)' }}>
      {sublabel}
    </p>
  </div>
);

const DoneBlock: React.FC<{
  previewUrl: string;
  downloadUnlocked: boolean;
  onSignupClick: () => void;
  onAuditionClick: () => void;
}> = ({ previewUrl, downloadUnlocked, onSignupClick, onAuditionClick }) => (
  <div className="flex flex-col gap-4">
    <div className="flex items-center gap-2">
      <CheckCircle className="w-4 h-4" style={{ color: 'var(--hj-green)' }} />
      <span className="mono-label" style={{ color: 'var(--hj-green)' }}>
        Preview ready
      </span>
    </div>
    <video
      src={previewUrl}
      controls
      autoPlay
      playsInline
      className="w-full"
      style={{ borderRadius: 'var(--hj-r-md)', background: '#000', maxHeight: 420 }}
    />
    <div className="flex flex-wrap items-center gap-2.5">
      {downloadUnlocked ? (
        <a
          href={previewUrl}
          download="hey-joe-demo.mp4"
          className="inline-flex items-center gap-1.5"
          style={{
            padding: '11px 20px',
            borderRadius: 999,
            background: 'var(--hj-blue)',
            border: '1px solid var(--hj-blue)',
            color: 'oklch(0.18 0.005 70)',
            fontSize: 14,
            fontWeight: 700,
            fontFamily: 'inherit',
            textDecoration: 'none',
            minHeight: 44,
          }}
        >
          Download preview
        </a>
      ) : (
        <button
          type="button"
          onClick={onSignupClick}
          className="inline-flex items-center gap-1.5"
          style={{
            padding: '11px 20px',
            borderRadius: 999,
            background: 'var(--hj-blue)',
            border: '1px solid var(--hj-blue)',
            color: 'oklch(0.18 0.005 70)',
            fontSize: 14,
            fontWeight: 700,
            fontFamily: 'inherit',
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          Sign up free to download
        </button>
      )}
      <button
        type="button"
        onClick={onAuditionClick}
        className="inline-flex items-center gap-1.5"
        style={{
          padding: '11px 18px',
          borderRadius: 999,
          background: 'transparent',
          border: '1px solid var(--hj-hairline)',
          color: 'var(--hj-ink-2)',
          fontSize: 14,
          fontWeight: 500,
          fontFamily: 'inherit',
          cursor: 'pointer',
          minHeight: 44,
        }}
      >
        <Play className="w-3.5 h-3.5" /> Run the full take through the Audition Enhancer
      </button>
    </div>
    <p className="m-0" style={{ fontSize: 11.5, color: 'var(--hj-ink-4)' }}>
      Demo previews carry a small Hey Joe watermark. Full clips run through the Audition Enhancer ship clean.
    </p>
  </div>
);

const FailedBlock: React.FC<{ errorMsg: string; onReset: () => void }> = ({ errorMsg, onReset }) => (
  <div
    className="flex flex-col items-center text-center"
    style={{
      padding: '40px 24px',
      borderRadius: 'var(--hj-r-md)',
      background: 'oklch(0.30 0.12 25 / 0.10)',
      border: '1px solid hsl(var(--destructive) / 0.4)',
    }}
  >
    <AlertCircle className="w-7 h-7" style={{ color: 'hsl(var(--destructive))' }} />
    <p
      className="font-hj-display m-0"
      style={{ marginTop: 10, fontSize: 18, color: 'var(--hj-ink)', letterSpacing: '-0.02em' }}
    >
      Demo failed
    </p>
    <p className="m-0" style={{ marginTop: 6, fontSize: 13.5, color: 'var(--hj-ink)', maxWidth: 440 }}>
      {errorMsg}
    </p>
    <button
      type="button"
      onClick={onReset}
      style={{
        marginTop: 16,
        padding: '8px 16px',
        borderRadius: 999,
        background: 'var(--hj-blue)',
        border: '1px solid var(--hj-blue)',
        color: 'oklch(0.18 0.005 70)',
        fontSize: 13,
        fontWeight: 700,
        fontFamily: 'inherit',
        cursor: 'pointer',
        minHeight: 40,
      }}
    >
      Try again
    </button>
  </div>
);

const RateLimitedBlock: React.FC<{
  msg: string;
  isAuthenticated: boolean;
  onSignupClick: () => void;
  onTrialClick: () => void;
}> = ({ msg, isAuthenticated, onSignupClick, onTrialClick }) => (
  <div
    className="flex flex-col gap-3"
    style={{
      padding: 22,
      borderRadius: 'var(--hj-r-md)',
      background: 'var(--hj-yellow-tint)',
      border: '1px solid var(--hj-blue)',
    }}
  >
    <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
      {msg}
    </p>
    <div className="flex flex-wrap gap-2">
      {!isAuthenticated && (
        <button
          type="button"
          onClick={onSignupClick}
          style={{
            padding: '9px 18px',
            borderRadius: 999,
            background: 'oklch(0.18 0.005 70)',
            border: '1px solid oklch(0.18 0.005 70)',
            color: 'var(--hj-bg)',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
            minHeight: 40,
          }}
        >
          Sign up free
        </button>
      )}
      <button
        type="button"
        onClick={onTrialClick}
        style={{
          padding: '9px 18px',
          borderRadius: 999,
          background: 'var(--hj-blue)',
          border: '1px solid var(--hj-blue)',
          color: 'oklch(0.18 0.005 70)',
          fontSize: 13,
          fontWeight: 700,
          fontFamily: 'inherit',
          cursor: 'pointer',
          minHeight: 40,
        }}
      >
        Start free trial
      </button>
    </div>
  </div>
);

export default HomeDemoSection;
